import React, { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import { parseLocalDate } from '../lib/dateFmt';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  MessageSquare, Settings, Briefcase, Users, FolderPlus,
  Loader2, ArrowRight, X, Save, Lock, Newspaper, Calendar, Tag, Clock, MapPin,
  Megaphone, Pin, Plus, Edit3, Trash2, Eye, Image as ImageIcon, ShieldAlert,
  Monitor, Smartphone,
} from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { useTranslation } from '../i18nContext';
import { useAuth } from '../lib/AuthContext';
import { canAction } from '../lib/rbac';
import MembershipStatusCard from '../components/MembershipStatusCard';
import { usePublicConfig } from '../lib/publicConfig';
import ImageUploader from '../components/ImageUploader';
import RichTextEditor from '../components/RichTextEditor';

// Text pasted from Word/PDF often carries non-breaking spaces (&nbsp; / U+00A0)
// between words, which prevent line wrapping and make the notice overflow its
// container. Normalise them to regular spaces at render time.
const cleanAvisoHtml = (h) =>
  DOMPurify.sanitize((h || '').replace(/&nbsp;/gi, ' ').replace(/\u00a0/g, ' '));

// Renders an aviso body, clamped to a few lines with a fade + "Leer más" link
// (to the dedicated detail page) when the content is long.
function AvisoBody({ html, to, isEs }) {
  const ref = React.useRef(null);
  const [state, setState] = React.useState({ measured: false, overflow: false });
  React.useEffect(() => {
    const el = ref.current;
    if (el) setState({ measured: true, overflow: el.scrollHeight > el.clientHeight + 4 });
  }, [html]);
  const clamp = !state.measured || state.overflow;
  return (
    <>
      <div className="mt-2 relative">
        <div
          ref={ref}
          className={`prose prose-sm max-w-none break-words text-charcoal-700 ${clamp ? 'max-h-28 overflow-hidden' : ''}`}
          dangerouslySetInnerHTML={{ __html: cleanAvisoHtml(html) }}
        />
        {state.overflow && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white to-transparent" aria-hidden="true" />
        )}
      </div>
      {state.overflow && (
        <Link
          to={to}
          data-testid="aviso-read-more"
          className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-cmic-600 hover:text-cmic-800 transition-colors"
        >
          {isEs ? 'Leer más' : 'Read more'} <ArrowRight size={14} aria-hidden="true" />
        </Link>
      )}
    </>
  );
}
import InternalAccessGuard from '../components/InternalAccessGuard';
import useForumAccess from '../components/forum/useForumAccess';

const ICON_MAP = {
  MessageSquare,
  Settings,
  Briefcase,
  Users,
};

function iconFor(name) {
  return ICON_MAP[name] || MessageSquare;
}

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, Math.floor((now - then) / 1000));
  if (diff < 60) return 'hace un instante';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 7) return `hace ${Math.floor(diff / 86400)} d`;
  return new Date(iso).toLocaleDateString();
}

const TABS = [
  { id: 'noticias', labelEs: 'Noticias internas', labelEn: 'Internal news', icon: Newspaper },
  { id: 'eventos', labelEs: 'Eventos internos', labelEn: 'Internal events', icon: Calendar },
  { id: 'avisos', labelEs: 'Avisos', labelEn: 'Notices', icon: Megaphone },
  { id: 'foro', labelEs: 'Foro', labelEn: 'Forum', icon: MessageSquare },
];

export default function ForumPage() {
  return (
    <InternalAccessGuard>
      <HubInner />
    </InternalAccessGuard>
  );
}

function HubInner() {
  const t = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const access = useForumAccess();
  // Read ?tab= from URL so external links (e.g. notification bell) can deep-link.
  const initialTab = useMemo(() => {
    const q = new URLSearchParams(location.search).get('tab');
    if (TABS.some((tb) => tb.id === q)) return q;
    let saved = null;
    try { saved = localStorage.getItem('cmic_hub_tab'); } catch (e) { /* ignore */ }
    return TABS.some((tb) => tb.id === saved) ? saved : 'noticias';
  }, [location.search]);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [categories, setCategories] = useState([]);
  const [latest, setLatest] = useState([]);
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [avisos, setAvisos] = useState([]);
  const [seen, setSeen] = useState({});
  const [membership, setMembership] = useState(null);
  const publicCfg = usePublicConfig();
  // Forum master switch: when OFF the "Foro" tab is hidden for EVERYONE
  // (including admin/council). Admins re-enable it from the admin panel.
  const forumHidden = publicCfg?.forum_enabled === false;
  const visibleTabs = useMemo(() => TABS.filter((tb) => tb.id !== 'foro' || !forumHidden), [forumHidden]);
  useEffect(() => {
    if (forumHidden && activeTab === 'foro') setActiveTab('noticias');
  }, [forumHidden, activeTab]);
  const showDues = !!(publicCfg && publicCfg.show_dues_on_profile);
  const [loading, setLoading] = useState(true);
  const [showNewCat, setShowNewCat] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState('');
  const [editingAviso, setEditingAviso] = useState(null);

  // RBAC: what can the current user create on each tab?
  const canCreateContent = canAction(user, 'content', 'create');
  const canEditContent = canAction(user, 'content', 'edit');
  const canDeleteContent = canAction(user, 'content', 'delete');
  const canCreateForum = canAction(user, 'forum', 'create');

  async function load() {
    setLoading(true);
    try {
      const [c, th, n, ev, av, sn] = await Promise.all([
        api.get('/forum/categories'),
        api.get('/forum/threads'),
        api.get('/internal/news').catch(() => ({ data: [] })),
        api.get('/internal/events').catch(() => ({ data: [] })),
        api.get('/internal/avisos').catch(() => ({ data: [] })),
        api.get('/me/seen').catch(() => ({ data: {} })),
      ]);
      setCategories(c.data || []);
      setLatest((th.data || []).slice(0, 6));
      setNews(n.data || []);
      setEvents(ev.data || []);
      setAvisos(av.data || []);
      setSeen(sn.data || {});
      // Membership card data — only meaningful for users with an interpreter
      // profile; admins/council without a member record just get no_profile.
      api.get('/me/dashboard')
        .then((r) => setMembership(r.data?.membership || null))
        .catch(() => setMembership(null));
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || 'Error');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  // Mark current tab as "seen" after viewing it for a moment so the badge clears
  // on the next visit. Maps tab -> seen key.
  useEffect(() => {
    const seenKey = { foro: 'forum', noticias: 'news', eventos: 'events', avisos: 'avisos' }[activeTab];
    if (!seenKey || loading) return;
    const timer = setTimeout(() => {
      api.post('/me/seen', { key: seenKey })
        .then(() => setSeen((s) => ({ ...s, [seenKey]: new Date().toISOString() })))
        .catch(() => {});
    }, 1500);
    return () => clearTimeout(timer);
  }, [activeTab, loading]);

  async function createCategory() {
    if (!form.name.trim()) return;
    setCreating(true); setErr('');
    try {
      await api.post('/forum/categories', form);
      setForm({ name: '', description: '' });
      setShowNewCat(false);
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || 'Error');
    } finally { setCreating(false); }
  }

  const localePrefix = `/${t.locale}/${t.locale === 'es' ? 'foro' : 'forum'}`;
  const isEs = t.locale === 'es';
  const dateLocale = isEs ? 'es-MX' : 'en-US';
  const formatDate = (s) => s ? parseLocalDate(s).toLocaleDateString(dateLocale, { year: 'numeric', month: 'long', day: 'numeric' }) : '';
  const formatTime = (s) => s ? new Date(s).toLocaleTimeString(dateLocale, { hour: '2-digit', minute: '2-digit' }) : '';

  // Sync the active tab into the URL (deep links) and remember it so the hub
  // reopens on the last visited tab next time.
  const switchTab = (id) => {
    setActiveTab(id);
    try { localStorage.setItem('cmic_hub_tab', id); } catch (e) { /* ignore */ }
    const params = new URLSearchParams(location.search);
    params.set('tab', id);
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
  };

  // ── Avisos CRUD ─────────────────────────────────────────────────────
  async function saveAviso(payload) {
    try {
      if (payload.id) {
        await api.put(`/internal/avisos/${payload.id}`, payload);
      } else {
        await api.post('/internal/avisos', payload);
      }
      setEditingAviso(null);
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || 'Error');
    }
  }
  async function deleteAviso(id) {
    if (!window.confirm(isEs ? '¿Eliminar este aviso?' : 'Delete this notice?')) return;
    try {
      await api.delete(`/internal/avisos/${id}`);
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || 'Error');
    }
  }
  // Open admin create routes in same window for News/Events. Avisos use inline modal.
  function createOnTab() {
    if (activeTab === 'noticias') navigate('/admin/news?new=1');
    else if (activeTab === 'eventos') navigate('/admin/events?new=1');
    else if (activeTab === 'avisos') setEditingAviso({ id: null, title: '', body_html: '', is_pinned: false, expires_at: '', category: '', audience: 'all', image_url: '' });
    else if (activeTab === 'foro') setShowNewCat(true);
  }
  const canCreateOnTab = (
    (activeTab === 'foro' && canCreateForum) ||
    (activeTab === 'avisos' && canCreateContent) ||
    (activeTab === 'noticias' && canCreateContent) ||
    (activeTab === 'eventos' && canCreateContent)
  );
  const createLabel = {
    foro: isEs ? 'Nueva categoría' : 'New category',
    avisos: isEs ? 'Nuevo aviso' : 'New notice',
    noticias: isEs ? 'Nueva noticia' : 'New news',
    eventos: isEs ? 'Nuevo evento' : 'New event',
  }[activeTab];

  const upcomingEvents = useMemo(() => events.filter((e) => !e.is_past), [events]);
  const pastEvents = useMemo(() => events.filter((e) => e.is_past), [events]);

  // Determine which items are "new" based on the user's last-seen timestamp.
  const seenForum = seen.forum || '1970-01-01T00:00:00Z';
  const seenNews = seen.news || '1970-01-01T00:00:00Z';
  const seenEvents = seen.events || '1970-01-01T00:00:00Z';
  const seenAvisos = seen.avisos || '1970-01-01T00:00:00Z';
  const isNew = (iso, baseline) => !!iso && iso > baseline;
  const unreadCounts = useMemo(() => ({
    foro: latest.filter((th) => isNew(th.last_activity_at, seenForum)).length,
    noticias: news.filter((n) => isNew(n.created_at || n.published_at, seenNews)).length,
    eventos: events.filter((e) => isNew(e.created_at, seenEvents)).length,
    avisos: avisos.filter((a) => isNew(a.created_at, seenAvisos)).length,
  }), [latest, news, events, avisos, seenForum, seenNews, seenEvents, seenAvisos]);

  const NewBadge = () => (
    <span className="inline-flex items-center bg-cmic-500 text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ml-2">
      {isEs ? 'Nuevo' : 'New'}
    </span>
  );

  return (
    <div data-testid="forum-page" className="bg-gray-50 min-h-[60vh] pt-20">
      <section className="relative overflow-hidden bg-gradient-to-br from-cmic-900 via-cmic-800 to-slate-900 text-white py-12">
        <div className="absolute -top-24 -right-16 w-80 h-80 bg-cmic-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-10 w-72 h-72 bg-cmic-400/10 rounded-full blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md ring-1 ring-white/15 px-3 py-1 rounded-full text-xs font-medium mb-4">
                <Lock size={12} /> {isEs ? 'Espacio privado del Colegio' : 'Private members area'}
              </div>
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-2">
                {isEs ? 'Hub Interno' : 'Internal Hub'}
              </h1>
              <p className="text-white/70 max-w-2xl leading-relaxed">
                {isEs
                  ? 'Noticias, eventos, avisos y foro del Colegio Mexicano de Intérpretes de Conferencias — solo para miembros activos.'
                  : 'News, events, notices and forum of the Mexican College of Conference Interpreters — members only.'}
              </p>
            </div>
            {canCreateOnTab && (
              <button
                onClick={createOnTab}
                data-testid={`hub-create-${activeTab}-btn`}
                className="inline-flex items-center gap-2 bg-white text-cmic-800 px-5 py-2.5 rounded-xl font-semibold text-sm hover:bg-white/90 transition-all duration-300 active:scale-[0.97] shadow-lg shadow-black/20"
              >
                <Plus size={16} /> {createLabel}
              </button>
            )}
          </div>
        </div>
      </section>

      {showDues && membership && membership.status !== 'no_profile' && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 -mb-2" data-testid="hub-membership">
          <MembershipStatusCard
            membership={membership}
            profileHref={`/${t.locale}/${isEs ? 'mi-perfil' : 'my-profile'}`}
            isEs={isEs}
          />
        </div>
      )}

      <div className="bg-gray-50/80 backdrop-blur-md border-b border-gray-200 sticky top-16 z-30" data-testid="forum-tabs">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <nav className="inline-flex bg-slate-100 p-1.5 rounded-2xl gap-1 overflow-x-auto max-w-full">
            {visibleTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              const count = unreadCounts[tab.id] || 0;
              return (
                <button
                  key={tab.id}
                  onClick={() => switchTab(tab.id)}
                  data-testid={`forum-tab-${tab.id}`}
                  className={`relative inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap rounded-xl transition-all duration-300 ${
                    isActive
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-900 hover:bg-slate-200/50'
                  }`}
                >
                  <Icon size={16} className={isActive ? 'text-cmic-600' : ''} />
                  {isEs ? tab.labelEs : tab.labelEn}
                  {count > 0 && (
                    <span
                      data-testid={`forum-tab-${tab.id}-badge`}
                      className="inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold bg-cmic-600 text-white rounded-full px-1.5 shadow-sm"
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {err && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">{err}</div>}
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-cmic-500" size={28} /></div>
        ) : (
          <>
            {activeTab === 'foro' && (
              <div className="grid lg:grid-cols-3 gap-6" data-testid="forum-tab-content-foro">
                <div className="lg:col-span-2">
                  <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-3">
                    {isEs ? 'Categorías' : 'Categories'}
                  </h2>
                  {categories.length > 0 && (
                    <ul className="bg-white border border-charcoal-200 rounded-xl overflow-hidden divide-y divide-charcoal-200" data-testid="forum-categories-list">
                      {categories.map((c) => {
                        const Icon = iconFor(c.icon);
                        return (
                          <li key={c.id}>
                            <Link
                              to={`${localePrefix}/c/${c.slug}`}
                              data-testid={`forum-category-${c.slug}`}
                              className="group flex flex-col sm:flex-row sm:items-center gap-4 p-5 hover:bg-charcoal-50 transition-colors"
                            >
                              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-cmic-50 text-cmic-600 flex items-center justify-center group-hover:bg-cmic-100 transition-colors" aria-hidden="true">
                                <Icon size={22} />
                              </div>
                              <div className="flex-grow min-w-0">
                                <div className="text-lg font-semibold text-charcoal-900 inline-flex items-center gap-2 flex-wrap">
                                  {c.name}
                                  {c.is_pinned && <span className="bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">{isEs ? 'oficial' : 'official'}</span>}
                                </div>
                                <p className="text-sm text-charcoal-500 mt-1 line-clamp-2">{c.description || (isEs ? 'Sin descripción' : 'No description')}</p>
                              </div>
                              <div className="flex-shrink-0 flex items-center gap-3 sm:gap-4 sm:ml-2 pl-16 sm:pl-0">
                                <div className="text-right">
                                  <div className="text-sm font-bold text-charcoal-800 leading-none">{c.thread_count}</div>
                                  <div className="text-[10px] text-charcoal-400 uppercase tracking-wider mt-1">{isEs ? 'hilos' : 'threads'}</div>
                                </div>
                                <ArrowRight size={18} className="text-charcoal-300 group-hover:text-cmic-500 group-hover:translate-x-1 transition-all duration-200" aria-hidden="true" />
                              </div>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {categories.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 px-6 text-center bg-slate-50/50 rounded-3xl border-2 border-dashed border-slate-200">
                      <MessageSquare size={40} className="text-slate-300 mb-4 p-3 bg-white rounded-full shadow-sm w-16 h-16" />
                      <p className="text-slate-500 max-w-sm">{isEs ? 'Aún no hay categorías. Crea la primera.' : 'No categories yet. Create the first one.'}</p>
                    </div>
                  )}
                </div>

                <aside className="space-y-3">
                  {access.is_moderator && (
                    <Link
                      to={`${localePrefix}/${isEs ? 'moderacion' : 'moderation'}`}
                      data-testid="forum-moderation-log-link"
                      className="flex items-center gap-2 bg-white border border-cmic-200 text-cmic-700 rounded-xl px-4 py-3 text-sm font-medium hover:bg-cmic-50 transition-all"
                    >
                      <ShieldAlert size={16} /> {isEs ? 'Registro de moderación' : 'Moderation log'}
                    </Link>
                  )}
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-charcoal-500 mb-1">
                    {isEs ? 'Actividad reciente' : 'Recent activity'}
                  </h2>
                  {latest.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-sm text-charcoal-500">
                      {isEs ? 'Aún no hay hilos publicados.' : 'No threads posted yet.'}
                    </div>
                  ) : (
                    latest.map((th) => (
                      <Link
                        key={th.id}
                        to={`${localePrefix}/t/${th.id}`}
                        data-testid={`forum-latest-${th.id}`}
                        className="block bg-white border border-gray-200 rounded-xl p-4 hover:border-cmic-300 hover:shadow-sm transition-all"
                      >
                        <div className="text-[10px] uppercase font-semibold text-cmic-600 mb-1">{th.category_name}</div>
                        <div className="text-sm font-semibold text-charcoal-800 line-clamp-2 flex items-start gap-1">
                          <span className="flex-1">{th.title}</span>
                          {isNew(th.last_activity_at, seenForum) && <NewBadge />}
                        </div>
                        <div className="text-[11px] text-charcoal-500 mt-1.5 flex items-center gap-2">
                          <span>{th.author_name}</span>
                          <span>·</span>
                          <span>{relativeTime(th.last_activity_at)}</span>
                          <span>·</span>
                          <span>{th.reply_count} {isEs ? 'resp.' : 'rep.'}</span>
                        </div>
                      </Link>
                    ))
                  )}
                  <div className="bg-cmic-50/50 border border-cmic-100 rounded-xl p-4 text-xs text-cmic-800">
                    {isEs ? 'Hola' : 'Hi'} <strong>{user?.full_name || user?.username}</strong>. {isEs ? 'Las publicaciones del foro son visibles únicamente para miembros con sesión activa.' : 'Forum posts are visible only to logged-in members.'}
                  </div>
                </aside>
              </div>
            )}

            {activeTab === 'noticias' && (
              <div data-testid="forum-tab-content-noticias">
                {news.length === 0 ? (
                  <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-charcoal-500">
                    <Newspaper size={40} className="mx-auto mb-3 text-charcoal-300" />
                    <p>{isEs ? 'Aún no hay noticias publicadas.' : 'No news posted yet.'}</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {news.map((n) => (
                      <article
                        key={n.id}
                        data-testid={`forum-news-${n.id}`}
                        className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-cmic-300 hover:shadow-sm transition-all flex flex-col"
                      >
                        {n.image_url && (
                          <div className="aspect-video overflow-hidden bg-gray-100">
                            <img src={n.image_url} alt={n.title} className="w-full h-full object-cover" />
                          </div>
                        )}
                        <div className="p-5 flex flex-col flex-1">
                          <div className="flex items-center gap-2 mb-2 text-xs text-charcoal-400">
                            {n.category && (
                              <span className="inline-flex items-center gap-1 bg-cmic-50 text-cmic-700 px-2 py-0.5 rounded-full font-medium">
                                <Tag size={10} /> {n.category}
                              </span>
                            )}
                            <span className="flex items-center gap-1"><Calendar size={11} /> {formatDate(n.published_at)}</span>
                          </div>
                          <h3 className="font-semibold text-charcoal-800 mb-2 line-clamp-2 flex items-start gap-1">
                            <span className="flex-1">{n.title}</span>
                            {isNew(n.created_at || n.published_at, seenNews) && <NewBadge />}
                          </h3>
                          {n.summary && <p className="text-sm text-charcoal-500 line-clamp-3 flex-1">{n.summary}</p>}
                          <Link
                            to={`/${t.locale}/${isEs ? 'noticias' : 'news'}`}
                            className="inline-flex items-center gap-1 text-sm font-medium text-cmic-600 hover:text-cmic-700 mt-3 group"
                          >
                            {isEs ? 'Leer más' : 'Read more'}
                            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                          </Link>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'eventos' && (
              <div data-testid="forum-tab-content-eventos" className="space-y-10">
                {upcomingEvents.length === 0 && pastEvents.length === 0 ? (
                  <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-charcoal-500">
                    <Calendar size={40} className="mx-auto mb-3 text-charcoal-300" />
                    <p>{isEs ? 'Aún no hay eventos programados.' : 'No events scheduled yet.'}</p>
                  </div>
                ) : (
                  <>
                    {upcomingEvents.length > 0 && (
                      <div>
                        <h2 className="text-xs font-semibold uppercase tracking-wider text-charcoal-500 mb-4">
                          {isEs ? 'Próximos eventos' : 'Upcoming events'}
                        </h2>
                        <div className="space-y-3">
                          {upcomingEvents.map((ev) => (
                            <div
                              key={ev.id}
                              data-testid={`forum-event-${ev.id}`}
                              className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col sm:flex-row gap-5 hover:border-cmic-300 hover:shadow-sm transition-all"
                            >
                              <div className="flex-shrink-0 w-16 h-16 bg-cmic-500 rounded-xl flex flex-col items-center justify-center text-white">
                                <span className="text-xl font-bold leading-none">{parseLocalDate(ev.event_date).getDate()}</span>
                                <span className="text-[10px] uppercase mt-1">{parseLocalDate(ev.event_date).toLocaleDateString(dateLocale, { month: 'short' })}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-charcoal-800 mb-1 flex items-center gap-1">
                                  <span>{ev.title}</span>
                                  {isNew(ev.created_at, seenEvents) && <NewBadge />}
                                </h3>
                                {ev.description && (
                                  <div className="text-sm text-charcoal-500 line-clamp-2 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(ev.description || "") }} />
                                )}
                                <div className="flex flex-wrap gap-3 text-xs text-charcoal-500 mt-2">
                                  <span className="flex items-center gap-1"><Clock size={11} className="text-cmic-500" /> {formatTime(ev.event_date)}</span>
                                  {ev.location && <span className="flex items-center gap-1"><MapPin size={11} className="text-cmic-500" /> {ev.location}</span>}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {pastEvents.length > 0 && (
                      <div>
                        <h2 className="text-xs font-semibold uppercase tracking-wider text-charcoal-500 mb-4">
                          {isEs ? 'Eventos pasados' : 'Past events'}
                        </h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {pastEvents.map((ev) => (
                            <div key={ev.id} className="bg-white rounded-xl border border-gray-200 p-4 opacity-90">
                              <p className="text-xs text-charcoal-400 mb-1">{formatDate(ev.event_date)}</p>
                              <h4 className="font-medium text-charcoal-700 text-sm mb-1">{ev.title}</h4>
                              {ev.location && <p className="text-xs text-charcoal-500 flex items-center gap-1"><MapPin size={10} /> {ev.location}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeTab === 'avisos' && (
              <div data-testid="forum-tab-content-avisos" className="space-y-4">
                {avisos.length === 0 ? (
                  <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-charcoal-500">
                    <Megaphone size={40} className="mx-auto mb-3 text-charcoal-300" />
                    <p>{isEs ? 'No hay avisos por el momento.' : 'No notices at the moment.'}</p>
                    {canCreateContent && (
                      <button
                        onClick={() => setEditingAviso({ id: null, title: '', body_html: '', is_pinned: false, expires_at: '', category: '', audience: 'all', image_url: '' })}
                        data-testid="hub-avisos-empty-create-btn"
                        className="inline-flex items-center gap-2 mt-4 bg-cmic-500 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-cmic-600"
                      >
                        <Plus size={14} /> {isEs ? 'Publicar el primer aviso' : 'Publish the first notice'}
                      </button>
                    )}
                  </div>
                ) : (
                  avisos.map((a) => (
                    <article
                      key={a.id}
                      data-testid={`hub-aviso-${a.id}`}
                      className={`bg-white border rounded-xl p-5 transition-all ${a.is_pinned ? 'border-amber-300 ring-1 ring-amber-100' : 'border-gray-200'}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${a.is_pinned ? 'bg-amber-100 text-amber-700' : 'bg-cmic-50 text-cmic-600'}`}>
                          {a.is_pinned ? <Pin size={18} /> : <Megaphone size={18} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1.5">
                            {a.category && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-wide uppercase bg-cmic-100 text-cmic-800 px-2 py-0.5 rounded-full">
                                <Tag size={9} /> {a.category}
                              </span>
                            )}
                            {a.is_pinned && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded">
                                <Pin size={9} /> {isEs ? 'Fijado' : 'Pinned'}
                              </span>
                            )}
                            {a.audience && a.audience !== 'all' && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase bg-charcoal-100 text-charcoal-600 px-2 py-0.5 rounded">
                                <Users size={9} /> {a.audience === 'admins' ? (isEs ? 'Solo admins' : 'Admins only') : (isEs ? 'Solo consejo' : 'Council only')}
                              </span>
                            )}
                          </div>
                          <h3 className="font-semibold text-charcoal-800">
                            {a.title}
                            {isNew(a.created_at, seenAvisos) && <NewBadge />}
                          </h3>
                          {a.image_url && (
                            <div className="mt-3 rounded-lg overflow-hidden border border-gray-100 max-h-56">
                              <img src={a.image_url} alt={a.title} className="w-full object-cover" />
                            </div>
                          )}
                          {a.body_html && (
                            <AvisoBody html={a.body_html} to={`${localePrefix}/aviso/${a.id}`} isEs={isEs} />
                          )}
                          <div className="text-[11px] text-charcoal-400 mt-3 flex items-center gap-2 flex-wrap">
                            <span>{a.author_name || (isEs ? 'CMIC' : 'CMIC')}</span>
                            <span>·</span>
                            <span>{relativeTime(a.created_at)}</span>
                            {a.expires_at && (
                              <>
                                <span>·</span>
                                <span className="inline-flex items-center gap-1"><Clock size={10} /> {isEs ? 'Expira' : 'Expires'} {a.expires_at}</span>
                              </>
                            )}
                          </div>
                          {a.forum_thread_id && (
                            <Link
                              to={`${localePrefix}/t/${a.forum_thread_id}`}
                              data-testid={`hub-aviso-comment-${a.id}`}
                              className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-cmic-600 hover:text-cmic-700 hover:underline"
                            >
                              <MessageSquare size={14} /> {isEs ? 'Comentar sobre este aviso' : 'Comment on this notice'}
                            </Link>
                          )}
                        </div>
                        {(canEditContent || canDeleteContent) && (
                          <div className="flex items-center gap-1">
                            {canEditContent && (
                              <button
                                onClick={() => setEditingAviso({
                                  id: a.id, title: a.title, body_html: a.body_html || '',
                                  is_pinned: a.is_pinned, expires_at: a.expires_at || '',
                                  category: a.category || '', audience: a.audience || 'all', image_url: a.image_url || '',
                                })}
                                data-testid={`hub-aviso-edit-${a.id}`}
                                title={isEs ? 'Editar' : 'Edit'}
                                className="p-1.5 rounded text-charcoal-500 hover:text-cmic-600 hover:bg-cmic-50"
                              >
                                <Edit3 size={14} />
                              </button>
                            )}
                            {canDeleteContent && (
                              <button
                                onClick={() => deleteAviso(a.id)}
                                data-testid={`hub-aviso-delete-${a.id}`}
                                title={isEs ? 'Eliminar' : 'Delete'}
                                className="p-1.5 rounded text-charcoal-500 hover:text-rose-600 hover:bg-rose-50"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </article>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </section>

      {editingAviso && (
        <AvisoModal
          aviso={editingAviso}
          onClose={() => setEditingAviso(null)}
          onSave={saveAviso}
          isEs={isEs}
        />
      )}

      {showNewCat && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowNewCat(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="font-semibold text-charcoal-800">Nueva categoría</h2>
              <button onClick={() => setShowNewCat(false)} className="p-1.5 rounded-lg text-charcoal-500 hover:bg-gray-100"><X size={16} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Nombre</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ej: Plataformas RSI"
                  data-testid="forum-new-cat-name"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Descripción</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="¿De qué se trata esta categoría?"
                  data-testid="forum-new-cat-desc"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500 resize-none"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
              <button onClick={() => setShowNewCat(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-charcoal-600 hover:bg-gray-100">Cancelar</button>
              <button
                onClick={createCategory}
                disabled={creating || !form.name.trim()}
                data-testid="forum-new-cat-save"
                className="inline-flex items-center gap-2 bg-cmic-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-cmic-600 disabled:opacity-60"
              >
                {creating ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Crear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AvisoModal({ aviso, onClose, onSave, isEs }) {
  const [form, setForm] = useState({
    id: aviso.id,
    title: aviso.title || '',
    body_html: aviso.body_html || '',
    is_pinned: !!aviso.is_pinned,
    expires_at: aviso.expires_at || '',
    category: aviso.category || '',
    audience: aviso.audience || 'all',
    image_url: aviso.image_url || '',
  });
  const [saving, setSaving] = useState(false);
  const [previewMobile, setPreviewMobile] = useState(false);

  // Close on ESC for keyboard users.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  async function submit(e) {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  }

  const T = (es, en) => (isEs ? es : en);
  const inputCls = 'w-full rounded-lg border border-charcoal-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/20 focus:border-cmic-500 transition-all';
  const AUDIENCE_OPTS = [
    { v: 'all', es: 'Todos los miembros', en: 'All members' },
    { v: 'council', es: 'Consejo', en: 'Council' },
    { v: 'admins', es: 'Admins', en: 'Admins' },
  ];
  const CAT_SUGGESTIONS = ['Asamblea', 'Cuotas', 'Eventos', 'Capacitación', 'Importante'];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col"
        data-testid="aviso-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-charcoal-100 flex-shrink-0">
          <h2 className="font-semibold text-charcoal-800 flex items-center gap-2">
            <Megaphone size={18} className="text-cmic-600" />
            {form.id ? T('Editar aviso', 'Edit notice') : T('Crear nuevo aviso', 'Create new notice')}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-charcoal-500 hover:bg-charcoal-100 transition-colors"><X size={16} /></button>
        </div>

        <form onSubmit={submit} className="grid lg:grid-cols-12 gap-6 p-6 overflow-y-auto overflow-x-hidden">
          {/* ── FORM (7) ─────────────────────────────────────────────── */}
          <div className="lg:col-span-7 min-w-0 space-y-4">
            <div>
              <label className="block text-xs font-semibold tracking-[0.04em] uppercase text-charcoal-500 mb-1.5">{T('Título', 'Title')}</label>
              <input
                type="text" value={form.title} onChange={(e) => set({ title: e.target.value })}
                data-testid="aviso-titulo-input" required maxLength={140}
                placeholder={T('Ej: Convocatoria a Asamblea General', 'E.g. Call to General Assembly')}
                className={inputCls}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold tracking-[0.04em] uppercase text-charcoal-500 mb-1.5">{T('Contenido', 'Content')}</label>
              <RichTextEditor
                value={form.body_html}
                onChange={(html) => set({ body_html: html })}
                placeholder={T('Escribe el cuerpo del aviso…', 'Write the notice body…')}
                testid="aviso-contenido-input"
                minHeight={200}
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold tracking-[0.04em] uppercase text-charcoal-500 mb-1.5">{T('Categoría', 'Category')}</label>
                <input
                  type="text" value={form.category} onChange={(e) => set({ category: e.target.value })}
                  data-testid="aviso-categoria-input" list="aviso-cat-list" maxLength={40}
                  placeholder={T('Opcional', 'Optional')} className={inputCls}
                />
                <datalist id="aviso-cat-list">
                  {CAT_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div>
                <label className="block text-xs font-semibold tracking-[0.04em] uppercase text-charcoal-500 mb-1.5">{T('Expiración', 'Expiration')}</label>
                <input
                  type="date" value={form.expires_at} onChange={(e) => set({ expires_at: e.target.value })}
                  data-testid="aviso-expiracion-picker" className={inputCls}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold tracking-[0.04em] uppercase text-charcoal-500 mb-1.5">{T('Audiencia', 'Audience')}</label>
              <div className="grid grid-cols-3 gap-2" data-testid="aviso-audiencia-radio">
                {AUDIENCE_OPTS.map((o) => {
                  const active = form.audience === o.v;
                  return (
                    <button
                      key={o.v} type="button" onClick={() => set({ audience: o.v })}
                      data-testid={`aviso-audiencia-${o.v}`}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                        active ? 'bg-cmic-600 text-white border-cmic-600 shadow-sm' : 'bg-white text-charcoal-600 border-charcoal-200 hover:border-cmic-300'
                      }`}
                    >
                      {isEs ? o.es : o.en}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold tracking-[0.04em] uppercase text-charcoal-500 mb-1.5">{T('Imagen adjunta (opcional)', 'Attached image (optional)')}</label>
              <ImageUploader
                value={form.image_url} onChange={(url) => set({ image_url: url || '' })}
                testid="aviso-adjuntos" shape="rect" skipCrop
                hint={T('Se mostrará dentro del aviso.', 'Shown inside the notice.')}
              />
            </div>

            <label className="flex items-center justify-between gap-3 cursor-pointer bg-charcoal-50 border border-charcoal-200 rounded-lg px-4 py-3">
              <span className="flex items-center gap-2 text-sm font-medium text-charcoal-700">
                <Pin size={14} className={form.is_pinned ? 'text-amber-500' : 'text-charcoal-400'} /> {T('Fijar / destacar aviso', 'Pin / highlight notice')}
              </span>
              <span
                role="switch" aria-checked={form.is_pinned} tabIndex={0}
                onClick={() => set({ is_pinned: !form.is_pinned })}
                onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); set({ is_pinned: !form.is_pinned }); } }}
                data-testid="aviso-fijar-switch"
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.is_pinned ? 'bg-amber-500' : 'bg-charcoal-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.is_pinned ? 'translate-x-6' : 'translate-x-1'}`} />
              </span>
            </label>
          </div>

          {/* ── LIVE PREVIEW (5) ─────────────────────────────────────── */}
          <div className="lg:col-span-5 min-w-0">
            <div className="lg:sticky lg:top-2">
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="text-xs font-semibold tracking-[0.05em] uppercase text-charcoal-500 flex items-center gap-1.5">
                  <Eye size={13} /> {T('Vista previa', 'Live preview')}
                </div>
                <div className="inline-flex rounded-lg border border-charcoal-200 overflow-hidden">
                  <button type="button" onClick={() => setPreviewMobile(false)} data-testid="aviso-preview-desktop"
                    title={T('Escritorio', 'Desktop')}
                    className={`p-1.5 transition-colors ${!previewMobile ? 'bg-cmic-600 text-white' : 'text-charcoal-500 hover:bg-charcoal-100'}`}>
                    <Monitor size={14} />
                  </button>
                  <button type="button" onClick={() => setPreviewMobile(true)} data-testid="aviso-preview-mobile"
                    title={T('Móvil', 'Mobile')}
                    className={`p-1.5 transition-colors ${previewMobile ? 'bg-cmic-600 text-white' : 'text-charcoal-500 hover:bg-charcoal-100'}`}>
                    <Smartphone size={14} />
                  </button>
                </div>
              </div>
              <div className={previewMobile ? 'mx-auto w-[340px] max-w-full rounded-[2rem] border-[8px] border-charcoal-800 bg-charcoal-800 shadow-xl p-1' : ''}>
              <div
                data-testid="aviso-preview-card"
                className={`bg-white ${previewMobile ? 'rounded-[1.6rem]' : 'rounded-xl'} shadow-md border p-5 ${form.is_pinned ? 'border-amber-300 ring-1 ring-amber-100' : 'border-charcoal-100'}`}
              >
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  {form.category && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-wide uppercase bg-cmic-100 text-cmic-800 px-2 py-0.5 rounded-full">
                      <Tag size={9} /> {form.category}
                    </span>
                  )}
                  {form.is_pinned && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded">
                      <Pin size={9} /> {T('Fijado', 'Pinned')}
                    </span>
                  )}
                  {form.audience !== 'all' && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase bg-charcoal-100 text-charcoal-600 px-2 py-0.5 rounded">
                      <Users size={9} /> {form.audience === 'admins' ? T('Solo admins', 'Admins only') : T('Solo consejo', 'Council only')}
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-bold text-charcoal-900 leading-tight mb-1">
                  {form.title || <span className="text-charcoal-300">{T('Título del aviso…', 'Notice title…')}</span>}
                </h3>
                {form.image_url && (
                  <div className="my-3 rounded-lg overflow-hidden border border-charcoal-100">
                    <img src={form.image_url} alt="" className="w-full h-32 object-cover" />
                  </div>
                )}
                {form.body_html ? (
                  <div className="prose prose-sm max-w-none break-words text-charcoal-600 mt-2" dangerouslySetInnerHTML={{ __html: cleanAvisoHtml(form.body_html) }} />
                ) : (
                  <p className="text-sm text-charcoal-300 mt-2">{T('El contenido aparecerá aquí…', 'Content will appear here…')}</p>
                )}
                <div className="flex items-center justify-between border-t border-charcoal-100 pt-3 mt-4 text-xs text-charcoal-400">
                  <span className="flex items-center gap-1"><Calendar size={11} /> {T('Hoy', 'Today')}</span>
                  {form.expires_at && (
                    <span className="flex items-center gap-1"><Clock size={11} /> {T('Expira', 'Expires')} {form.expires_at}</span>
                  )}
                </div>
              </div>
              </div>
            </div>
          </div>

          {/* ── ACTIONS (full width) ─────────────────────────────────── */}
          <div className="lg:col-span-12 flex justify-end gap-2 pt-4 border-t border-charcoal-100">
            <button type="button" onClick={onClose} data-testid="aviso-cancelar-btn" className="px-5 py-2.5 rounded-lg text-sm font-medium text-charcoal-600 hover:bg-charcoal-100 transition-colors">
              {T('Cancelar', 'Cancel')}
            </button>
            <button
              type="submit" disabled={saving || !form.title.trim()} data-testid="aviso-publicar-btn"
              className="inline-flex items-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white px-6 py-2.5 rounded-lg text-sm font-semibold shadow-sm transition-colors disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {form.id ? T('Guardar cambios', 'Save changes') : T('Publicar aviso', 'Publish notice')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

